import { DomainError } from '../errors.js';
import {
  type RaMatrixDefinition,
  type RiskRating,
  ratingIn,
  STANDARD_RISK_MATRIX,
} from '../risk/matrix.js';
import {
  HSE_OPEN_STATUSES,
  HSE_REPORT_TYPES,
  type HseFindingStatus,
  type HseqStage,
  type HseStatus,
  ROOT_CAUSE_MIN_LENGTH,
} from './constants.js';
import type { HseReportInput, HseRuleOptions } from './report.js';

/** Kapanış onayının durumu (`deriveApprovalState`'in özeti). */
export type HseClosureState = 'draft' | 'pending' | 'approved' | 'returned' | 'reapproval_pending';

export interface HseStatusInput {
  submittedAt: string | null;
  reviewedAt: string | null;
  /** Silinmemiş faaliyetler. */
  actions: readonly { status: string; createdAt: string }[];
  closure: HseClosureState;
  /** Son iadenin ya da yeniden açmanın zamanı (`closure = returned`). */
  returnedAt: string | null;
}

/**
 * Görünen durum (ADR 0049 §3): saklanmaz, kayıttaki zamanlardan, faaliyetlerden ve kapanış
 * onayından türer. Kapanış iade edildiyse (doğrulama başarısız ya da yeniden açıldı) ve o andan
 * sonra yeni faaliyet açılmadıysa `reopened`; eski kayıt ve iade gerekçesi geçmişte kalır.
 */
export function deriveHseStatus(input: HseStatusInput): HseStatus {
  if (input.submittedAt === null) return 'draft';
  if (input.closure === 'approved') return 'closed';
  if (input.closure === 'pending' || input.closure === 'reapproval_pending') {
    return 'pending_verification';
  }
  const live = input.actions.filter((a) => a.status !== 'cancelled');
  if (input.closure === 'returned') {
    const since = input.returnedAt ?? '';
    if (!live.some((a) => a.createdAt > since)) return 'reopened';
  }
  if (input.reviewedAt === null) return 'submitted';
  if (live.length === 0) return 'under_review';
  if (live.every((a) => a.status === 'completed')) return 'pending_verification';
  if (live.every((a) => a.status === 'planned')) return 'action_assigned';
  return 'in_progress';
}

/**
 * Görünen durumun dört ana aşaması (ortak 2026-10-20b H1; ADR 0097 §3): taslak ve gönderilmiş
 * "Reported"; inceleme, faaliyet ve yeniden açılmış "Under Action"; doğrulama bekleyen
 * "Verification"; kapanmış "Closed".
 */
export function hseqStageOf(status: HseStatus): HseqStage {
  switch (status) {
    case 'draft':
    case 'submitted':
      return 'reported';
    case 'pending_verification':
      return 'verification';
    case 'closed':
      return 'closed';
    default:
      return 'under_action';
  }
}

/** Uygunsuzluğun ihlal edilen maddesi karşılaştırma için (büyük/küçük harf, boşluk, nokta). */
export function ncClauseKey(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const key = value
    .normalize('NFC')
    .toLocaleLowerCase('en')
    .replace(/[\s.,;:/\\-]+/g, ' ')
    .trim();
  return key === '' ? null : key;
}

/**
 * Tekrar eden uygunsuzluk (H5; ADR 0097 §5): aynı gemide, ihlal edilen maddesi aynı olan başka
 * uygunsuzluk raporları (taslaklar ve kendisi hariç). Madde boşsa aynı kategori.
 */
export function isRecurrentNc(
  report: { id: string; vesselId: string; clause: unknown; category: string | null },
  other: {
    id: string;
    vesselId: string;
    reportType: string;
    clause: unknown;
    category: string | null;
    submitted: boolean;
  },
): boolean {
  if (other.id === report.id || other.vesselId !== report.vesselId) return false;
  if (other.reportType !== 'non_conformity' || !other.submitted) return false;
  const mine = ncClauseKey(report.clause);
  if (mine !== null) return ncClauseKey(other.clause) === mine;
  return report.category !== null && other.category === report.category;
}

const OPEN_ACTION = new Set(['planned', 'in_progress']);

/**
 * Geciken rapor (ADR 0049 §5): kapanmamış ve taslak olmayan; hedef tarihi gemi gününe göre geçmiş
 * ya da tamamlanmamış faaliyetinin hedef tarihi geçmiş.
 */
export function isHseOverdue(
  report: {
    status: HseStatus;
    targetDate: string | null;
    actions: readonly { status: string; dueDate: string | null }[];
  },
  today: string,
): boolean {
  if (report.status === 'draft' || report.status === 'closed') return false;
  if (report.targetDate !== null && report.targetDate < today) return true;
  return report.actions.some((a) => isActionOverdue(a, today));
}

export function isActionOverdue(
  action: { status: string; dueDate: string | null },
  today: string,
): boolean {
  return OPEN_ACTION.has(action.status) && action.dueDate !== null && action.dueDate < today;
}

/** PSC remark'ının ya da bulgunun durumu: bağlı faaliyetlerinden. */
export function findingStatus(actions: readonly { status: string }[]): HseFindingStatus {
  const live = actions.filter((a) => a.status !== 'cancelled');
  if (live.length === 0) return 'open';
  if (live.every((a) => a.status === 'completed')) return 'closed';
  if (live.every((a) => a.status === 'planned')) return 'planned';
  return 'in_progress';
}

export interface HseKpis {
  total: number;
  open: number;
  pendingReview: number;
  overdue: number;
  closed: number;
}

/**
 * Liste kartları (ADR 0049 §11; ortak §1): toplam, açık, inceleme bekleyen (kaptan incelemesi ya
 * da kapanış kararı), geciken ve kapanan.
 */
export function hseKpis(
  items: readonly { status: HseStatus; overdue: boolean; awaitingReview: boolean }[],
): HseKpis {
  return {
    total: items.length,
    open: items.filter((i) => HSE_OPEN_STATUSES.includes(i.status)).length,
    pendingReview: items.filter((i) => i.awaitingReview).length,
    overdue: items.filter((i) => i.overdue).length,
    closed: items.filter((i) => i.status === 'closed').length,
  };
}

/* ---------- Faaliyet ---------- */

/**
 * Faaliyet kanıtsız tamamlanmaz (ortak §7; ADR 0049 §5): en az bir canlı ek ya da bağlı,
 * raporlanmış PMS iş emri / gönderilmiş form kaydı; tamamlama notu zorunlu.
 */
export function assertActionCompletable(input: { note: string | null; evidence: number }): void {
  if (input.note === null || input.note.trim() === '') {
    throw new DomainError('COMPLETION_NOTE_REQUIRED');
  }
  if (input.evidence < 1) throw new DomainError('EVIDENCE_REQUIRED');
}

/** Faaliyetin geçişi: başla, tamamla, iptal; tamamlanan ya da iptal edilen değişmez. */
export function assertActionTransition(
  from: string,
  to: 'in_progress' | 'completed' | 'cancelled',
): void {
  const allowed =
    (to === 'in_progress' && from === 'planned') ||
    (to === 'completed' && (from === 'planned' || from === 'in_progress')) ||
    (to === 'cancelled' && (from === 'planned' || from === 'in_progress'));
  if (!allowed) throw new DomainError('INVALID_ACTION_TRANSITION', `${from} → ${to}`);
}

/* ---------- Kapanış ---------- */

export interface ClosureReport extends HseReportInput {
  submittedAt: string | null;
  reviewedAt: string | null;
}

const filled = (value: unknown, min = 1) => typeof value === 'string' && value.trim().length >= min;

/**
 * Kapanışın eksikleri (ADR 0049 §4): Verification & Closure sekmesi listeyi gösterir, gönderimin
 * doğrulayıcısı boş olmasını ister. Kodlar arayüzde `hse.closure.gaps.*`.
 */
export function hseClosureGaps(
  report: ClosureReport,
  actions: readonly { kind: string; status: string; findingId: string | null }[],
  findings: readonly { id: string }[],
  options: HseRuleOptions = {},
): string[] {
  const gaps: string[] = [];
  if (report.submittedAt === null) gaps.push('NOT_SUBMITTED');
  else if (report.reviewedAt === null) gaps.push('NOT_REVIEWED');
  const live = actions.filter((a) => a.status !== 'cancelled');
  if (live.length === 0) gaps.push('NO_ACTIONS');
  else if (live.some((a) => a.status !== 'completed')) gaps.push('ACTIONS_OPEN');
  const details = (report.details ?? {}) as Record<string, unknown>;
  const type = report.reportType;
  // [ISM 9.2] tekrarını önleme: uygunsuzluk, kaza ve near miss en az bir önleyici faaliyet ister
  const preventive = live.some((a) => a.kind === 'preventive');
  if (type !== 'deficiency' && !preventive) gaps.push('PREVENTIVE_ACTION');
  if (type !== 'near_miss' && !filled(report.rootCause, ROOT_CAUSE_MIN_LENGTH)) {
    gaps.push('ROOT_CAUSE');
  }
  if (!filled(report.verificationNote)) gaps.push('VERIFICATION_NOTE');
  // H5, H2 Verification (ADR 0097 §5): uygunsuzlukta etkinlik doğrulaması "evet" olmalı; diğer
  // türlerde "etkin değil" denmişse kapanmaz. Eski kayıtta alan boştur, yalnız uygunsuzlukta istenir
  const effective = ((details.verification ?? null) as { effective?: unknown } | null)?.effective;
  if (!options.legacy && (type === 'non_conformity' ? effective !== 'yes' : effective === 'no')) {
    gaps.push('EFFECTIVENESS');
  }
  if (type === 'non_conformity') {
    const covered = new Set(live.map((a) => a.findingId).filter((f) => f !== null));
    if (findings.some((f) => !covered.has(f.id))) gaps.push('FINDING_WITHOUT_ACTION');
  }
  if (type === 'deficiency') {
    // Arıza giderildi demek yetmez: test sonucu ve hizmete uygunluk (ortak §4B)
    if (!filled(details.testResult)) gaps.push('TEST_RESULT');
    if (details.fitForService !== 'yes') gaps.push('FIT_FOR_SERVICE');
  }
  if (type === 'near_miss') {
    if (!filled(report.lessonsLearned)) gaps.push('LESSONS_LEARNED');
    // Kaynak kaldırıldı ya da risk kabul edilebilir seviyeye indi (ortak §4C)
    if (details.residualRisk !== 'low' && details.residualRisk !== 'medium') {
      gaps.push('RESIDUAL_RISK');
    }
  }
  if (type === 'accident') {
    if (!filled(report.investigation)) gaps.push('INVESTIGATION');
    if (!filled(report.lessonsLearned)) gaps.push('LESSONS_LEARNED');
    const notification = (details.notification ?? null) as {
      decision?: string | null;
      items?: { notifiedAt?: string | null }[];
    } | null;
    const decision = notification?.decision ?? null;
    if (decision !== 'required' && decision !== 'not_required') {
      gaps.push('NOTIFICATION_DECISION');
    } else if (decision === 'required') {
      const items = notification?.items ?? [];
      if (items.length === 0 || items.some((item) => !item.notifiedAt)) {
        gaps.push('NOTIFICATION_PENDING');
      }
    }
  }
  return gaps;
}

export function assertHseClosable(
  report: ClosureReport,
  actions: readonly { kind: string; status: string; findingId: string | null }[],
  findings: readonly { id: string }[],
  options: HseRuleOptions = {},
): void {
  const gaps = hseClosureGaps(report, actions, findings, options);
  if (gaps.length > 0)
    throw new DomainError('HSE_NOT_CLOSABLE', `HSE_NOT_CLOSABLE ${gaps.join(',')}`, gaps.join(','));
}

export const isHseReportType = (value: unknown): value is (typeof HSE_REPORT_TYPES)[number] =>
  typeof value === 'string' && (HSE_REPORT_TYPES as readonly string[]).includes(value);

/**
 * HSEQ risk değerlendirmesinin seviyesi (H2 Risk Assessment; ADR 0097 §4): olasılık × şiddet,
 * şirketin seçili risk matrisinde (ADR 0085); matris verilmezse standart 4 bant. Biri boşsa `null`.
 */
export function hseqRiskRating(
  risk: { likelihood?: unknown; severity?: unknown } | null | undefined,
  matrix: RaMatrixDefinition = STANDARD_RISK_MATRIX,
): RiskRating | null {
  const likelihood = risk?.likelihood;
  const severity = risk?.severity;
  if (typeof likelihood !== 'number' || typeof severity !== 'number') return null;
  try {
    return ratingIn(matrix, likelihood, severity);
  } catch {
    return null;
  }
}
